"use strict";

class RobustCart {
  constructor(rootEl, country, currency, cartUrl, cartAddUrl, cartChangeUrl) {
    this.rootEl = rootEl;
    this.country = country;
    this.currency = currency;
    this.cartUrl = cartUrl;
    this.cartAddUrl = cartAddUrl;
    this.cartChangeUrl = cartChangeUrl;
    this.scrollPosition = window.scrollY;

    this.log("Initializing");
    this.addAllListeners();
    this.log("Initialized");
    this.refreshCart();
    this.log("Initial cart refreshed!")
  }

  log(message) {
    if (window.ROBUST_CART_DEV) {
      console.log(
        `%cRobust Cart: ${message}`,
        "background: #cd302b; color: #fff",
        this
      );
    }
  }

  createAddToCartPromise(form) {    
    let formData = new FormData(form);
    let data = Object.fromEntries(formData.entries());

    const sellingPlanEl = document.getElementById('selling_plan')?.value || form.querySelector("input[name='selling_plan']")?.value;
    if(Boolean(sellingPlanEl) && sellingPlanEl != "undefined") {
      data.selling_plan = sellingPlanEl;
    } else {
      delete data.selling_plan;
    }
    
    return fetch(this.cartAddUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }

  createFetchCartPromise() {
    return fetch("/?section_id=robust-cart");
  }

  createUpdateCartItemPromise(id, quantity) {
    return fetch(this.cartChangeUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, quantity }),
    });
  }

  createAddCartItemPromise(id, quantity) {
    return fetch(this.cartAddUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, quantity }),
    });
  }

  openCart() {
    this.rootEl.classList.add("rc90--open");
    this.scrollPosition = window.scrollY;
    this.lockScroll();
    initAll();
  }

  closeCart() {
    this.rootEl.classList.remove("rc90--open");
    this.unlockScroll();
    if (this._cartTrigger && typeof this._cartTrigger.focus === 'function') {
      this._cartTrigger.focus();
    }
  }

  async refreshCart() {
    let response = await this.createFetchCartPromise();
    let html = await response.text();
    let container = document.createElement("div");
    container.innerHTML = html;
    let content = container.querySelector(".rc90-box");
    this.rootEl.querySelector(".rc90-box").innerHTML = content.innerHTML;
    this.addInnerListeners();
    initAll();
    try {
      if (simplyInsurance && typeof simplyInsurance !== "undefined")
        simplyInsurance.loadApp();
    } catch (e) {
      console.log(e);
    }

    // Restore focus to the quantity button that triggered this refresh
    if (this._pendingFocusVariantId && this._pendingFocusType) {
      const selector = `[data-item-id="${this._pendingFocusVariantId}"] .rc90-item-main-footer-quantity-${this._pendingFocusType}`;
      const target = this.rootEl.querySelector(selector);
      if (target) {
        target.focus({ preventScroll: true });
      }
      this._pendingFocusVariantId = null;
      this._pendingFocusType = null;
    }
  }

  async removeCartItem(id) {
    await this.createUpdateCartItemPromise(id, 0);
    await this.refreshCart();
  }

  async changeCartItemQuantity(id, quantity) {
    await this.createUpdateCartItemPromise(id, quantity);
    await this.refreshCart();
    initAll();
  }

  getCartJson() {
    let dataEl = this.rootEl.querySelector(".robust-cart-json");
    if (dataEl) {
      let jsonData = dataEl.getAttribute("data-cart-json");
      if (jsonData) {
        return JSON.parse(jsonData);
      }
    }
  }

  addAllListeners() {
    this.addOpenCartListeners();
    this.addCartBoxListener();
    this.addInnerListeners();
    this.addLinkSubmissionListeners();
  }

  addInnerListeners() {
    this.addCloseCartListeners();
    this.addFormSubmissionListeners();
    this.addItemRemoveListeners();
    this.addItemQuantityListeners();
  }

  addCartBoxListener() {
    this.log("Adding cart box click listener");
    let box = this.rootEl.querySelector(".rc90-box");
    if (box) {
      box.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }
  }

  async addUpsellToCart(upsellProductId, quantity) {
    try {
      // Adding the upsell product to the cart via API.
      await this.createAddCartItemPromise(upsellProductId, quantity);

      // Refreshing the cart UI without page reload.
      await this.refreshCart();

      document
        .querySelector(`[data-item-id='${upsellProductId}'] .upsell__checkbox`)
        .click();
    } catch (error) {
      console.error("Error adding upsell to cart:", error);
      // Handle error (e.g., show a message to the user).
    }
  }

  addOpenCartListeners() {
    this.log("Adding open click listeners");
    document
      .querySelectorAll(`[href="${this.cartUrl}"], [href="/cart"]`)
      .forEach((element) => {
        element.addEventListener("click", (e) => {
          e.preventDefault();
          this._cartTrigger = element;
          this.openCart();
        });
      });
  }

  addCloseCartListeners() {
    this.log("Adding close click listeners");
    this.rootEl.addEventListener("click", this.closeCart.bind(this));
    this.rootEl
      .querySelectorAll(
        ".rc90-header-close, .rc90-buttons-continue-shopping, .rc90-empty-inner-continue"
      )
      .forEach((element) => {
        element.addEventListener("click", this.closeCart.bind(this));
      });
  }

  addLinkSubmissionListeners() {
    this.log("Adding link submission listeners");

    document.querySelectorAll('a[href*="/cart/add"]').forEach((link) => {
      link.addEventListener("click", async (e) => {
        e.preventDefault(); // Prevent the default link behavior
        this._cartTrigger = link;

        const urlParams = new URL(link.href).searchParams;
        const id = urlParams.get("id");
        const quantity = parseInt(urlParams.get("quantity")) || 1; // Default to 1 if quantity is not specified
        const sellingPlan = urlParams.get("selling_plan"); // Extract the selling_plan parameter

        console.log("sellingPlan", sellingPlan);

        if (id) {
          const data = { id, quantity };
          // If selling_plan parameter exists, add it to the data object
          if (sellingPlan) data.selling_plan = sellingPlan;

          await this.createAddCartItemPromiseWithData(data);
          await this.refreshCart();
          this.openCart();
        }
      });
    });
  }

  createAddCartItemPromiseWithData(data) {
    return fetch(this.cartAddUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }

  addFormSubmissionListeners() {

    // Check if the event listener has already been added.
    if (!this.formSubmissionListenerAdded) {
      document.addEventListener(
        "click",
        async (e) => {
          if (
            e.target.matches(
              `form[action="${this.cartAddUrl}"] button[name="add"], form[action="${this.cartAddUrl}"] button[name="add"] *`
            )
          ) {
            e.preventDefault();
            this._cartTrigger = e.target.closest('button[name="add"]') || e.target;
            let form = e.target.closest(`form[action="${this.cartAddUrl}"]`);
            await this.createAddToCartPromise(form);
            await this.refreshCart();
            this.openCart();
            var closeBtn = this.rootEl.querySelector('.rc90-header-close');
            if (closeBtn) closeBtn.focus();
          }
        },
        false
      );

      // Set a flag to indicate that the event listener has been added.
      this.formSubmissionListenerAdded = true;
    }
  }

  addItemRemoveListeners() {
    this.rootEl
      .querySelectorAll(".rc90-item-main-header-remove")
      .forEach((element) => {
        element.addEventListener("click", () => {
          let id = element.getAttribute("data-line-item-key");
          if (id) {
            this.removeCartItem(id);
          }
        });
      });
  }

  addItemQuantityListeners() {
    this.rootEl
      .querySelectorAll(
        ".rc90-item-main-footer-quantity-plus, .rc90-item-main-footer-quantity-minus"
      )
      .forEach((element) => {
        const isPlus = element.classList.contains(
          "rc90-item-main-footer-quantity-plus"
        );
        
        element.addEventListener("click", async () => {
          const id = element.getAttribute("data-line-item-key");
          const quantity = Number(element.getAttribute("data-quantity"));
          
          if (id && quantity) {
            const newQuantity = isPlus ? quantity + 1 : quantity - 1;

            // Capture focus target before the innerHTML reset wipes the button
            const variantId = element.closest("[data-item-id]")?.getAttribute("data-item-id");
            this._pendingFocusVariantId = variantId || null;
            this._pendingFocusType = isPlus ? "plus" : "minus";
            
            showOverlay();
            await this.changeCartItemQuantity(id, newQuantity);
            hideOverlay();
          }
        });
      });
  }

  lockScroll() {
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
  }

  unlockScroll() {
    document.body.style.overflow = "";
    document.body.style.touchAction = "";
  }
}

// Subscription Feature

document.addEventListener("DOMContentLoaded", initAll);

function initAll() {
  document.querySelectorAll(".rc90-item").forEach((item) => {
    const variantId = item.dataset.itemId;

    const sellingPlanCheckbox = item.querySelector(
      `.upsell__checkbox[data-variant-id="${variantId}"]`
    );
    const sellingPlanOptions = item.querySelector(`.upsell__options`);

    if (
      sellingPlanCheckbox &&
      sellingPlanOptions &&
      !item.dataset.listenerAdded
    ) {
      sellingPlanCheckbox.addEventListener(
        "change",
        handleCheckboxChange.bind(null, sellingPlanCheckbox, sellingPlanOptions)
      );
      sellingPlanOptions.addEventListener(
        "change",
        handleOptionsChange.bind(null, sellingPlanCheckbox, sellingPlanOptions)
      );

      item.dataset.listenerAdded = true;
    }
  });
}

async function handleCheckboxChange(sellingPlanCheckbox, sellingPlanOptions) {
  const variantId = sellingPlanCheckbox.dataset.variantId;
  showOverlay();
  if (sellingPlanCheckbox.checked) {
    sellingPlanOptions.style.display = "block";
    await addOrUpdateSellingPlan(
      variantId,
      sellingPlanOptions.options[0].value
    );
    await window.robustCart.refreshCart();
  } else {
    sellingPlanOptions.style.display = "none";
    await removeSellingPlan(variantId, sellingPlanOptions.value);
    await new Promise(resolve => setTimeout(resolve, 500));
    await window.robustCart.refreshCart();
  }
  setTimeout(() => {
    hideOverlay();
    updateFreeShippingBar();
  }, 1000);
}

async function handleOptionsChange(sellingPlanCheckbox, sellingPlanOptions) {
  const variantId = sellingPlanCheckbox.dataset.variantId;
  if (sellingPlanCheckbox.checked) {
    showOverlay();
    await addOrUpdateSellingPlan(variantId, sellingPlanOptions.value);
    await window.robustCart.refreshCart();
    setTimeout(() => {
      hideOverlay();
    }, 1000);
  }
}

function updateFreeShippingBar() {
  fetch("/?section_id=robust-cart")
    .then((response) => {
      return response.text();
    })
    .then((html) => {
      // Parse to HTML
      const parser = new DOMParser();
      const source = parser.parseFromString(html, "text/html");
      document.querySelector(".progress-bar-wrapper").innerHTML =
        source.querySelector(".progress-bar-wrapper").innerHTML;
    })
}

async function addOrUpdateSellingPlan(variant, plan) {
  const cart = await getCart();
  const lineItemWithPlan = findLineItemWithPlan(cart, variant);
  const lineItemWithoutPlan = findLineItemWithoutPlan(cart, variant);
  let quantity;

  if (lineItemWithoutPlan) {
    quantity = lineItemWithoutPlan.quantity;
  } else if (lineItemWithPlan) {
    quantity = lineItemWithPlan.quantity;
  } else {
    quantity = 1;
  }

  if (lineItemWithPlan) await removeLineItem(lineItemWithPlan.key);
  if (lineItemWithoutPlan) await removeLineItem(lineItemWithoutPlan.key);
  const result = await addLineItemPlan(variant, plan, quantity);


  document.querySelectorAll(`[data-item-id="${variant}"]`).forEach((item) => {
    if (!item.querySelector(".rc90-item-main-upsell")) item.remove();
  });

  return result;
}

async function removeSellingPlan(variant, plan) {
  const cart = await getCart();
  const lineItemWithPlan = findLineItemWithPlan(cart, variant);

  if (lineItemWithPlan) {
    await removeLineItem(lineItemWithPlan.key);
    await addLineItem(variant, lineItemWithPlan.quantity);
  }
}

async function getCart() {
  const result = await fetch("/cart.json");
  if (result.status === 200) return result.json();
  throw new Error(
    `Failed to get request, Shopify returned ${result.status} ${result.statusText}`
  );
}

function findLineItemWithPlan(cart, variant) {
  return cart.items.find(
    (item) => item.id === parseInt(variant) && item.selling_plan_allocation
  );
}

function findLineItemWithoutPlan(cart, variant) {
  return cart.items.find(
    (item) => item.id === parseInt(variant) && !item.selling_plan_allocation
  );
}

function removeLineItem(key) {
  return postToShopify("cart/change.js", {
    id: key,
    quantity: 0,
  });
}

function addLineItemPlan(variant, plan, quantity) {
  return postToShopify("cart/add.js", {
    items: [
      {
        quantity: quantity,
        id: variant,
        selling_plan: plan,
      },
    ],
  });
}

function addLineItem(variant, quantity) {
  return postToShopify("cart/add.js", {
    items: [
      {
        quantity: quantity,
        id: variant,
      },
    ],
  });
}

function postToShopify(endpoint, formData) {
  return fetch(window.Shopify.routes.root + endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(formData),
  })
    .then((response) => {
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    })
    .then((data) => {
      return data;
    })
}

async function updatePrice(variant) {
  const cart = await getCart();

  const item = cart.items.find((item) => item.id === parseInt(variant));

  let cartPrice = cart.total_price;
  let itemPrice = item.final_line_price;
  let originalItemPrice = item.original_line_price;

  const autoDiscountPercent = parseInt(
    document.querySelector(`[data-item-id="${variant}"]`)?.dataset.autoDiscountPercent || '0',
    10
  ) || 0;
  const hasSellingPlan = !!item.selling_plan_allocation;
  const shouldShowDiscount = autoDiscountPercent > 0 && hasSellingPlan;
  
  if (!shouldShowDiscount) {
    itemPrice = item.final_line_price;
    originalItemPrice = item.original_line_price;
  } else {
    const basePrice = item.original_line_price;
    const discountAmount = Math.round((basePrice * autoDiscountPercent) / 100);
    originalItemPrice = basePrice;
    itemPrice = basePrice - discountAmount;
  }

  const itemFormattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(itemPrice / 100);

  const originalItemFormattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(originalItemPrice / 100);

  const cartFormattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cartPrice / 100);

  const pricesContainer = document.querySelector(
    `[data-item-id="${variant}"] .rc90-item-main-footer-prices`
  );
  
  if (pricesContainer) {
    let compareElement = pricesContainer.querySelector('.rc90-item-main-footer-prices-compare');
    
    const hasRegularComparePrice = originalItemPrice > itemPrice;
    const shouldShowComparePrice = shouldShowDiscount || hasRegularComparePrice;
    
    if (shouldShowComparePrice) {
      if (!compareElement) {
        compareElement = document.createElement('span');
        compareElement.className = 'rc90-item-main-footer-prices-compare';
        pricesContainer.insertBefore(compareElement, pricesContainer.firstChild);
      }
      compareElement.innerText = originalItemFormattedPrice;
    } else {
      if (compareElement) {
        compareElement.remove();
      }
    }
    
    const priceElement = pricesContainer.querySelector('.rc90-item-main-footer-prices-price');
    if (priceElement) {
      priceElement.innerText = itemFormattedPrice;
    }
  }

  document.querySelector(
    ".rc90-totals-row:last-child .rc90-totals-row-value"
  ).innerText = cartFormattedPrice;
  
  // Update quantity button attributes with new line item key
  const itemElement = document.querySelector(`[data-item-id="${variant}"]`);
  if (itemElement) {
    const plusButton = itemElement.querySelector('.rc90-item-main-footer-quantity-plus');
    const minusButton = itemElement.querySelector('.rc90-item-main-footer-quantity-minus');
    const quantityDisplay = itemElement.querySelector('.rc90-item-main-footer-quantity-value');
    
    if (plusButton) {
      plusButton.setAttribute('data-line-item-key', item.key);
      plusButton.setAttribute('data-quantity', item.quantity);
    }
    
    if (minusButton) {
      minusButton.setAttribute('data-line-item-key', item.key);
      minusButton.setAttribute('data-quantity', item.quantity);
    }
    
    if (quantityDisplay) {
      quantityDisplay.textContent = item.quantity;
    }
  }
}

function showOverlay() {
  var overlay = document.getElementById("cart-overlay");
  var cartBox = document.querySelector(".rc90-box");
  if (overlay && cartBox) {
    overlay.style.height = cartBox.scrollHeight + "px";
    overlay.style.display = "flex";
  }
}

function hideOverlay() {
  document.getElementById("cart-overlay").style.display = "none";
}
